import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarBlank } from 'phosphor-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useFavoritesIds } from '@/hooks/useFavorites';
import { GoldenRule } from '@/components/ui/GoldenRule';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { PageHeader } from '@/components/ui/PageHeader';
import { Segmented } from '@/components/ui/Segmented';
import { categoryOf } from '@/constants/categories';
import { formatDayShort, formatTime, hasKnownTime } from '@/lib/dates';
import type { EventRow } from '@/types/database';
import { isSameDay } from 'date-fns';

export default function Agenda() {
  const { user } = useAuth();
  const fav = useFavoritesIds();
  const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming');
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Clé stable sur le contenu des favoris → recharge quand on ajoute/retire ailleurs.
  const favKey = Array.from(fav.ids).sort().join(',');

  useEffect(() => {
    let alive = true;
    (async () => {
      if (fav.ids.size === 0) { setEvents([]); setLoading(false); return; }
      setLoading(true);
      const ids = Array.from(fav.ids);
      const now = new Date().toISOString();
      const q = supabase.from('events').select('*').in('id', ids);
      // « À venir » inclut les événements en cours (date de fin future) même si
      // leur date de début est passée (marchés à l'année, manifestations multi-jours).
      // « Passés » doit donc les exclure : terminé si ends_at < now, ou si
      // starts_at < now quand ends_at est inconnu (sinon doublon dans les deux onglets).
      if (tab === 'upcoming') q.or(`starts_at.gte.${now},ends_at.gte.${now}`).order('starts_at', { ascending: true });
      else q.or(`ends_at.lt.${now},and(ends_at.is.null,starts_at.lt.${now})`).order('starts_at', { ascending: false });
      const { data } = await q;
      if (alive) { setEvents((data as EventRow[]) ?? []); setLoading(false); }
    })();
    return () => { alive = false; };
  }, [tab, user?.id, favKey]);

  return (
    <div className="bg-paper text-ink-1 min-h-screen pb-tabbar">
      <PageHeader eyebrow="Votre" title="Agenda" italicWord="agenda" />
      <div className="px-5 mb-6"><Segmented value={tab} onChange={v => setTab(v as 'upcoming' | 'past')}
        options={[{ id: 'upcoming', label: 'À venir' }, { id: 'past', label: 'Passés' }]} /></div>

      {loading ? (
        <div className="px-5 space-y-4">{[0, 1, 2].map(i => <Skeleton key={i} className="h-24 w-full" />)}</div>
      ) : events.length === 0 ? (
        <div className="px-5"><EmptyState icon={<CalendarBlank size={28} />} title="Votre agenda est vierge"
          body="Ajoutez des événements à vos favoris pour les retrouver ici."
          cta="Découvrir" onCta={() => location.assign('/decouvrir')} /></div>
      ) : (
        <Timeline events={events} />
      )}
    </div>
  );
}

function Timeline({ events }: { events: EventRow[] }) {
  // Group by date
  const groups: { date: Date; events: EventRow[] }[] = [];
  for (const e of events) {
    const d = new Date(e.starts_at);
    const last = groups[groups.length - 1];
    if (last && isSameDay(last.date, d)) last.events.push(e);
    else groups.push({ date: d, events: [e] });
  }
  const today = new Date();

  return (
    <div className="px-5 space-y-1">
      {groups.map(({ date, events: evs }, gi) => {
        const isToday = isSameDay(date, today);
        const dl = formatDayShort(date.toISOString()).toUpperCase().split(' ');
        return (
          <div key={gi}>
            {isToday && (
              <div className="flex items-center gap-3 mt-6 mb-3">
                <GoldenRule />
                <span className="t-ed-md italic whitespace-nowrap text-terre">Aujourd'hui</span>
                <GoldenRule />
              </div>
            )}
            <div className="flex gap-4 py-3 border-b border-paper-edge">
              <div className="w-16 shrink-0 text-center pt-1">
                <div className="t-mono-xs text-ink-3">{dl[0]}</div>
                <div className="font-serif text-[28px] leading-none mt-1">{dl[1]}</div>
                <div className="t-mono-xs text-ink-3 mt-1">{dl[2]}</div>
              </div>
              <div className="flex-1 space-y-3">
                {evs.map(e => {
                  const meta = categoryOf(e.kind);
                  return (
                    <Link key={e.id} to={`/event/${e.id}`} className="block">
                      <div className="t-cap" style={{ color: `rgb(var(${meta.colorVar}))` }}>{meta.label}</div>
                      <div className="t-ed-md leading-tight">{e.title}</div>
                      <div className="t-mono-xs text-ink-3 mt-0.5">{hasKnownTime(e.starts_at) ? `${formatTime(e.starts_at)} · ` : ''}{e.city ?? '—'}</div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
